import { brand } from '@enp/brand';
import { Badge, BrandMark, Button, FormField, Input, StatusBadge, toast } from '@enp/ui';
import { useQueryClient } from '@tanstack/react-query';
import { createFileRoute, redirect, useRouter } from '@tanstack/react-router';
import { CircleAlert, LogIn } from 'lucide-react';
import { useState, type FormEvent } from 'react';
import { SignInError } from '../auth/providers.js';
import { safeRedirect } from '../auth/redirect.js';
import { signIn } from '../auth/session.js';
import { getToken } from '../auth/token-store.js';
import { NetworkError } from '../lib/api.js';
import { useDocumentTitle } from '../shell/page.js';

interface LoginSearch {
  redirect?: string;
  reason?: 'expired' | 'signed-out';
}

export const Route = createFileRoute('/login')({
  validateSearch: (search: Record<string, unknown>): LoginSearch => ({
    redirect: typeof search['redirect'] === 'string' ? search['redirect'] : undefined,
    reason:
      search['reason'] === 'expired' || search['reason'] === 'signed-out'
        ? search['reason']
        : undefined,
  }),
  beforeLoad: () => {
    if (getToken()) throw redirect({ to: '/' });
  },
  component: LoginPage,
});

const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function LoginPage() {
  useDocumentTitle('Sign in');
  const { auth } = Route.useRouteContext();
  const search = Route.useSearch();
  const router = useRouter();
  const queryClient = useQueryClient();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [devAccessCode, setDevAccessCode] = useState('');
  const [fieldErrors, setFieldErrors] = useState<{ email?: string; password?: string }>({});
  const [formError, setFormError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const onSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const errors: typeof fieldErrors = {};
    const trimmed = email.trim();
    if (!trimmed) errors.email = 'Enter your email address.';
    else if (!EMAIL.test(trimmed)) errors.email = 'Enter an email address like name@bakery.co.za.';
    if (auth.needsPassword && !password) errors.password = 'Enter your password.';
    setFieldErrors(errors);
    setFormError(null);
    if (Object.keys(errors).length > 0) return;

    setSubmitting(true);
    let me: Awaited<ReturnType<typeof signIn>>;
    try {
      me = await signIn(auth, queryClient, {
        email: trimmed,
        password,
        devAccessCode: auth.offersDevAccessCode ? devAccessCode : undefined,
      });
    } catch (error) {
      if (error instanceof SignInError) setFormError(error.message);
      else if (error instanceof NetworkError) {
        // The server address only helps a developer; people get the plain version.
        setFormError(
          import.meta.env.DEV
            ? `${error.message} Check that the API is running, then try again.`
            : 'Can’t reach the server. Check your connection, then try again.',
        );
      } else {
        setFormError(
          `Something went wrong on our side, so you’re not signed in yet. Try again in a moment; if it keeps happening, email ${brand.supportEmail}.`,
        );
      }
      return;
    } finally {
      setSubmitting(false);
    }
    // Outside the try: signed in by now, so a navigation problem must never read as a
    // failed sign-in (CR8B-28).
    router.history.push(safeRedirect(search.redirect));
    toast.success('Signed in', { description: `Welcome back to ${me.account.name}.` });
  };

  return (
    <div className="grid min-h-dvh bg-surface lg:grid-cols-[minmax(0,1.1fr)_minmax(0,1fr)]">
      <aside className="relative hidden flex-col justify-between overflow-hidden border-r border-border bg-surface-sunken p-12 lg:flex">
        <BrandMark size="md" suffix={brand.appNames.admin} />
        <div className="flex max-w-lg flex-col gap-4">
          <p className="font-display text-4xl font-semibold leading-tight tracking-tight text-fg">
            {brand.tagline}
          </p>
          <p className="text-lg text-muted">
            Expiry, production and purchasing in one calm place, with the counter and the kitchen
            still working when the internet drops.
          </p>
        </div>
        <p className="text-sm text-muted">
          Need help?{' '}
          <a
            className="font-medium text-primary-text underline-offset-4 hover:underline"
            href={`mailto:${brand.supportEmail}`}
          >
            {brand.supportEmail}
          </a>
        </p>
        <img
          src={brand.inline.monochrome}
          alt=""
          className="pointer-events-none absolute -bottom-24 -right-20 size-96 opacity-[0.06]"
        />
      </aside>

      {/* Top-anchored: messages appearing above the form never make it jump. */}
      <main className="flex justify-center px-6 pb-12 pt-16 lg:pt-[18vh]">
        <div className="flex w-full max-w-sm flex-col gap-8">
          <BrandMark size="md" suffix={brand.appNames.admin} className="lg:hidden" />
          <div className="flex flex-col gap-2">
            <h1 className="font-display text-3xl font-semibold tracking-tight text-fg">Sign in</h1>
            <p className="text-body text-muted">
              Welcome back to {brand.productName} {brand.appNames.admin}.
            </p>
          </div>

          {search.reason === 'expired' && (
            <StatusBadge
              status="warning"
              className="w-full justify-start rounded-md px-3 py-2 text-sm"
            >
              Your session ended. Sign in again to carry on where you were.
            </StatusBadge>
          )}
          {search.reason === 'signed-out' && (
            <StatusBadge
              status="success"
              className="w-full justify-start rounded-md px-3 py-2 text-sm"
            >
              You’ve signed out.
            </StatusBadge>
          )}

          <form noValidate onSubmit={onSubmit} className="flex flex-col gap-5" aria-label="Sign in">
            <FormField label="Email" error={fieldErrors.email} required>
              <Input
                type="email"
                name="email"
                autoComplete="username"
                autoFocus
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="name@bakery.co.za"
              />
            </FormField>
            {auth.needsPassword && (
              <FormField label="Password" error={fieldErrors.password} required>
                <Input
                  type="password"
                  name="password"
                  autoComplete="current-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                />
              </FormField>
            )}
            {auth.offersDevAccessCode && (
              <FormField
                label="Dev access code"
                description="Needed on other devices on the network: DEV_ACCESS_CODE in .env.local on the computer running the API."
              >
                <Input
                  type="password"
                  name="dev-access-code"
                  autoComplete="off"
                  value={devAccessCode}
                  onChange={(e) => setDevAccessCode(e.target.value)}
                />
              </FormField>
            )}

            {formError && (
              <div
                role="alert"
                className="flex items-start gap-2.5 rounded-md border border-state-danger/35 bg-state-danger-subtle px-3 py-2.5 text-sm text-fg"
              >
                <CircleAlert aria-hidden className="mt-0.5 size-4 shrink-0 text-state-danger" />
                <span>{formError}</span>
              </div>
            )}

            <Button
              type="submit"
              size="lg"
              loading={submitting}
              icon={<LogIn aria-hidden />}
              className="w-full"
            >
              {submitting ? 'Signing in…' : 'Sign in'}
            </Button>
          </form>

          {auth.kind === 'dev' && (
            <p className="flex flex-wrap items-center gap-2 text-sm text-muted">
              <Badge tone="accent">Local development</Badge>
              {auth.offersDevAccessCode
                ? 'Email and the dev access code: no password.'
                : 'Email only: the local API signs you in without a password.'}
            </p>
          )}
        </div>
      </main>
    </div>
  );
}
